import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";
import {
    Box,
    Button,
    Checkbox,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    Grid,
    Stack,
} from "@mzc-pdc/ui";
import Typography from "@components_v2/typography/typography";
import theme from "@styles/theme";
import { IconClose } from "@mzc-cloudplex/icons";
import uniqid from "uniqid";
import TooltipButton from "../../../components_v2/tooltip-button/tooltip-button";
import UploadProvider from "@components_v2/upload-provider/upload-provider";
import {
    COMMON_JOB_STATUS,
    CONSTANTS,
    JOB_PANEL_TABS,
    MEDIA_TYPE_EXTENSIONS,
    MEDIA_TYPES,
    UPLOAD_FILE_LOCATION_TYPE,
    UPLOAD_LOCATION,
    UPLOAD_STATUS,
} from "@constants";
import compareVersions, {
    VERSION_CM_14175_ASSET_LOCK_WHILE_CREATE,
    VERSION_CM_14991_ASSET_GROUPED_BY_MEDIA_TYPE_SETTINGS,
    VERSION_CM_15200_PROJECT_MULTI_ACCOUNT,
} from "@cores/version";
import { getAssetCreateRequestMessage, getAssetMultiCreateRequestMessage } from "@cores/notification/message";
import { setNotification } from "@modules/notification";
import { getExtensionFromName, getMediaTypeFromExtension, getMediaTypeFromMimeType } from "@lib/mime/mime";
import DialogSelectS3Files from "@features/dialog/dialog-select-s3-files";
import DialogSelectS3FilesV2 from "@features/dialog/dialog-select-s3-files-v2";
import useVirtualFolderPath from "@hooks/useVirtualFolderPath";
import {
    createAsset as createAssetData,
    reset as createAssetReset,
    updateData,
    updateState,
} from "@modules/createAsset";
import { bulkCreateAssetElements, createOwnerLock } from "@modules/asset";
import {
    makeUploadQueueJob,
    retryUploadQueueJob,
    uploadFilesFinished,
    uploadJobFiles,
    uploadJobFileSetStatus,
} from "@modules/uploadQueue";
import useCustomContent from "@hooks/useCustomContent";
import useMyJobs from "@hooks/useMyJobs";
import { getDriveData } from "@modules/folderTreeView";
import useSettings from "@hooks/useSettings";
import StickyHeaderDialog from "@components_v2/dialog/sticky-header-dialog";
import VersionedComponent from "@components/VersionedComponent";
import menuService from "@services/menuService";

import CreateAssetFileSection from "./create-asset-file-section";
import CreateAssetLocationSection from "./create-asset-location-section";
import CreateAssetMetadataSection from "./create-asset-metadata-section";
import CreateAssetCustomMetadataSection from "./create-asset-custom-metadata-section";

const LOCATION_TYPE = UPLOAD_LOCATION.CREATE_ASSET;

const REGEXP_SCHEME = new RegExp("^(s3|https)://", "i");

const validateSelectedFile = (file) => {
    if (!file) {
        return null;
    }
    const mediaType = getMediaTypeFromExtension(getExtensionFromName(file.name));
    const acceptFileExtension = MEDIA_TYPE_EXTENSIONS[mediaType];

    if (!acceptFileExtension.includes("*")) {
        const REGEXP_EXTENSION = new RegExp(`.(${acceptFileExtension.join("|")})$`, "i");
        if (!REGEXP_EXTENSION.test(file.name)) {
            return CONSTANTS("FILE_VALIDATE_ERROR_MESSAGE").NOT_ALLOWED_EXTENSION;
        }
    }

    if (file.url && !REGEXP_SCHEME.test(file.url)) {
        return CONSTANTS("FILE_VALIDATE_ERROR_MESSAGE").NOT_ALLOWED_URL_PATTERN;
    }

    if (file.size <= 0) {
        return CONSTANTS("FILE_VALIDATE_ERROR_MESSAGE").INVALID_FILE;
    }

    return null;
};

const tryGetMimeType = (file) => {
    const { name } = file;
    if (name.endsWith(".exr")) {
        return "image/x-exr";
    }
    if (name.endsWith(".tga")) {
        return "image/x-tga";
    }
    return "application/octet-stream";
};

export const DialogCreateAsset = ({
    open,
    assetItems,
    initialFolder,
    initialCategory,
    initialCustomMetadata,
    onClose,
    onRefresh,
}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const store = useStore();
    const { pathname } = useLocation();
    const { spaceId } = menuService.parse(pathname);
    const { checkIsNull } = useCustomContent();
    const { setLocalStorageRecentLocation } = useVirtualFolderPath({ domain: "ASSET" });
    const { createJobData, updateMyJobsData, updateMyJobsTaskData, openMyJobs } = useMyJobs({
        resource: "createAsset",
        store: "uploadQueue",
    });
    const { domainStatus, getSettingData } = useSettings({ type: "DomainStatus" });

    const [pendingCreate, setPendingCreate] = useState(false);
    const [s3FilesModalOpen, setS3FilesModalOpen] = useState(false);

    const isAtomy = spaceId === "atomy-singapore";
    const [isOneAsset, setIsOneAsset] = useState(!isAtomy);

    const stage = useSelector(({ stage }) => stage);
    const project = useSelector(({ project }) => project);

    const isOwnerLocked = useSelector(({ createAsset }) => createAsset.data.isOwnerLocked);
    const selectedFilesEmpty = useSelector(({ createAsset }) => Object.keys(createAsset.selectedFiles).length === 0);
    const validationErrorsExist = useSelector(
        ({ createAsset }) => Object.keys(createAsset.validationErrors).length > 0,
    );
    const contentSchemaMetadataFields = useSelector(
        ({ createAsset }) => createAsset.data.contentSchemaMetadata?.fields,
    );
    const customMetaDataRequiredError = useMemo(() => {
        if (!contentSchemaMetadataFields) return false;
        return contentSchemaMetadataFields.some((el) => el.validationError && checkIsNull(el.value));
    }, [contentSchemaMetadataFields, checkIsNull]);

    const revokeBlobUrls = useCallback(() => {
        const { createAsset } = store.getState();
        createAsset.originFiles?.forEach((file) => {
            if (file.accessUrl && file.locationType === UPLOAD_FILE_LOCATION_TYPE.LOCAL) {
                URL.revokeObjectURL(file.accessUrl);
            }
        });
    }, [store]);

    const onChangeFiles = (files) => {
        const selectedFiles = files;
        if (!selectedFiles || Object.keys(selectedFiles).length === 0) {
            return;
        }

        const { createAsset } = store.getState();
        const currentOriginFiles = createAsset.originFiles ?? [];
        const currentName = createAsset.data?.name ?? "";
        const count = currentOriginFiles.length;

        const newFiles = Object.keys(selectedFiles).reduce((acc, key, currentIndex) => {
            const selectedFile = selectedFiles[key];

            selectedFile.key = String(count + currentIndex);
            selectedFile.displayName = `${currentName}${selectedFile.name.slice(
                0,
                selectedFile.name.lastIndexOf("."),
            )}`;
            selectedFile.mimeType = selectedFile.type?.length > 0 ? selectedFile.type : tryGetMimeType(selectedFile);
            selectedFile.locationType = selectedFile.locationType ?? UPLOAD_FILE_LOCATION_TYPE.LOCAL;
            selectedFile.validationError = validateSelectedFile(selectedFile);

            if (
                selectedFile.locationType === UPLOAD_FILE_LOCATION_TYPE.LOCAL &&
                ["IMAGE"].includes(getMediaTypeFromMimeType(selectedFile.type))
            ) {
                selectedFile.accessUrl = URL.createObjectURL(selectedFile);
            }

            acc.push(selectedFile);
            return acc;
        }, []);

        dispatch(updateState({ originFiles: [...currentOriginFiles, ...newFiles] }));
    };

    const updateRecentLocation = useCallback(() => {
        const { createAsset } = store.getState();
        const folderId = createAsset.data?.virtualFolder?.id;
        setLocalStorageRecentLocation({ folderId });
    }, [store, setLocalStorageRecentLocation]);

    const checkAndUploadFiles = async (uploadQueue, locationType, jobKey, mediaType, elements) => {
        const targets = uploadQueue[locationType].filter((item) => item.key === jobKey)[0];

        const selectedFiles = targets?.files;

        const filePut = async (selectedFile) =>
            await dispatch(
                uploadJobFiles({
                    locationType,
                    id: selectedFile.id,
                    key: jobKey,
                    uploadCompleteCallback: () => {},
                    type: "Asset",
                }),
            );

        const fileAlreadyUploaded = async (selectedFile) =>
            await dispatch(
                uploadJobFileSetStatus({
                    locationType,
                    id: selectedFile.id,
                    key: jobKey,
                    status: "COMPLETE",
                    uploadCompleteCallback: () => {},
                }),
            );

        let status = "SUCCEED";

        for (let i = 0; i < selectedFiles?.length; i++) {
            const selectedFile = selectedFiles[i];
            if (selectedFile.status === "COMPLETE") {
                fileAlreadyUploaded(selectedFile);
                continue;
            }

            const filePutResponse = await filePut(selectedFile);
            status = filePutResponse.status;

            const element = elements[i];
            if (element) {
                if (element.locationType === UPLOAD_FILE_LOCATION_TYPE.LOCAL) {
                    element.displayName = selectedFiles[i].displayName.split(".")[0];
                    mediaType === MEDIA_TYPES.VIDEO || mediaType === MEDIA_TYPES.AUDIO
                        ? (element.urls = [filePutResponse.url])
                        : (element.url = filePutResponse.url);
                }
            }

            if (status === "CANCELED") {
                return "CANCELED";
            } else if (status === "ERROR") {
                return "ERROR";
            }
        }

        return "COMPLETE";
    };

    const retryUpload = async ({ key, mediaType, elements }) => {
        const locationType = LOCATION_TYPE;
        const { retryKey, uploadQueue } = await dispatch(retryUploadQueueJob({ locationType, key }));

        const results = await checkAndUploadFiles(uploadQueue, locationType, retryKey, mediaType, elements);

        if (results === "SUCCEED" || results === "COMPLETE") {
            dispatch(uploadFilesFinished({ locationType, key: retryKey, status: UPLOAD_STATUS.COMPLETE }));
        } else if (results === "CANCELED") {
            dispatch(uploadFilesFinished({ locationType, key: retryKey, status: UPLOAD_STATUS.CANCELED }));
        } else if (results === "ERROR") {
            dispatch(uploadFilesFinished({ locationType, key: retryKey, status: UPLOAD_STATUS.ERROR }));
        }

        return results;
    };

    const showRequestMessage = (name, count) => {
        dispatch(
            setNotification({
                type: "info",
                contents:
                    count > 0
                        ? getAssetMultiCreateRequestMessage({ name, count })
                        : getAssetCreateRequestMessage({ name }),
            }),
        );
    };

    const onHandleCloseAddAsset = () => {
        onClose();
    };

    const createAssetAsync = useCallback(async () => {
        if (pendingCreate) return;
        let jobId = null;

        try {
            setPendingCreate(true);

            const { createAsset, folderTreeView } = store.getState();

            let virtualFolderId = createAsset.data?.virtualFolder?.id;
            if (!virtualFolderId) {
                if (folderTreeView?.driveList?.data?.length > 0) {
                    virtualFolderId = folderTreeView?.driveList?.data?.find(
                        (el) => el.resourceType?.type === "ASSET",
                    )?.[0]?.rootFolder?.id;
                } else {
                    const driveList = await dispatch(
                        getDriveData({
                            headerParams: { projectId: project.id },
                            domain: "ASSET",
                        }),
                    );
                    virtualFolderId = driveList?.[0]?.rootFolder?.id;
                }
            }

            jobId = createJobData({
                action: "UPLOAD_ASSET",
                metadata: {
                    folderId: virtualFolderId,
                },
                taskCount: createAsset.elementsByMediaType.length,
                isFolded: true,
            }).jobId;

            let count = 0;
            const element = createAsset.elementsByMediaType;
            const elementKey = element.length > 0 ? element[0].key : null;

            element?.map(() => {
                count += createAsset.selectedFiles[elementKey].length;
            });
            if (count > 0) count -= 1;
            const name = element?.[0]?.name ?? "";

            showRequestMessage(name, count);
            onHandleCloseAddAsset();
            updateRecentLocation();
            setPendingCreate(false);
            openMyJobs(JOB_PANEL_TABS.ASSET_JOBS);

            let partialFailure = false;

            await Promise.allSettled(
                createAsset.elementsByMediaType?.map(async (groupElement, i) => {
                    try {
                        const { key, ...newGroupElement } = groupElement;

                        const groupElementName = newGroupElement.name ?? "";

                        let elementsOfS3File = createAsset.elements[key]?.filter(
                            (el) => el.locationType === UPLOAD_FILE_LOCATION_TYPE.S3,
                        );

                        let elementsOfLocalFile =
                            createAsset.elements[key]?.filter(
                                (el) => el.locationType === UPLOAD_FILE_LOCATION_TYPE.LOCAL,
                            ) ?? [];

                        elementsOfS3File?.map((e) => {
                            delete e.key;
                            delete e.name;
                            if (
                                groupElement.mediaType === MEDIA_TYPES.VIDEO ||
                                groupElement.mediaType === MEDIA_TYPES.AUDIO
                            ) {
                                e.displayName = e.urls[0].match(/([^/]+)(?=\.\w+$)/)[0];
                            } else {
                                e.displayName = e.url.match(/([^/]+)(?=\.\w+$)/)[0];
                            }
                        });

                        const parsedAssetData = {
                            ...createAsset.data,
                            ...newGroupElement,
                            name: groupElementName,
                            extendedMetadata: {
                                contentSchemaId: createAsset.data.contentSchemaMetadata?.id,
                                data: {
                                    fields: createAsset.data.contentSchemaMetadata?.fields
                                        ?.filter((el) => el.isEditable && !checkIsNull(el.value))
                                        ?.map((meta) => ({
                                            id: meta.id,
                                            value: meta.value,
                                        })),
                                },
                            },
                            elements: elementsOfS3File,
                        };

                        const response = await dispatch(createAssetData(parsedAssetData, false));

                        if (elementsOfLocalFile.length) {
                            if (i === 0) updateMyJobsData({ jobId, data: { status: COMMON_JOB_STATUS.RUNNING } });

                            const createdAssetId = response.data.id;
                            const { version } = response.data;

                            const selectedFiles = createAsset.selectedFiles[key].filter(
                                (el) => el.locationType === UPLOAD_FILE_LOCATION_TYPE.LOCAL,
                            );

                            elementsOfLocalFile?.map((e) => {
                                delete e.key;
                                delete e.name;
                            });

                            const files = selectedFiles?.map((item) => {
                                item.id = uniqid();
                                return item;
                            });

                            const { name, key: newGroupElementKey, ...rest } = newGroupElement;
                            const jobKey = await dispatch(
                                makeUploadQueueJob({
                                    locationType: LOCATION_TYPE,
                                    files,
                                    resourceId: createdAssetId,
                                    data: {
                                        job: { jobId },
                                        ...createAsset.data,
                                        elements: elementsOfLocalFile,
                                        version,
                                        id: createdAssetId,
                                        name: name,
                                        metadata: { ...rest },
                                    },
                                }),
                            );

                            const result = await retryUpload({
                                key: jobKey,
                                mediaType: groupElement.mediaType,
                                elements: elementsOfLocalFile,
                            });

                            if (result === "SUCCEED" || result === "COMPLETE") {
                                await dispatch(
                                    bulkCreateAssetElements(createdAssetId, version, {
                                        elements: elementsOfLocalFile,
                                    }),
                                );
                            } else if (result === "ERROR") {
                                throw new Error("error");
                            }
                        }
                        if (createAsset.data.isOwnerLocked) {
                            const params = { assetIds: [response.data.id] };
                            await dispatch(createOwnerLock({ isLocked: false, params }));
                        }
                    } catch (e) {
                        partialFailure = true;
                        updateMyJobsTaskData({ jobId, taskIndex: i, data: { status: UPLOAD_STATUS.ERROR } });
                        console.log(e);
                        throw e;
                    }
                }),
            );

            updateMyJobsData({
                jobId,
                data: { status: UPLOAD_STATUS.COMPLETE, endAt: new Date().toISOString(), partialFailure },
            });
        } catch (error) {
            updateMyJobsData({
                jobId,
                data: { status: UPLOAD_STATUS.COMPLETE, endAt: new Date().toISOString(), partialFailure: true },
            });
            console.error(`error`, error);
            setPendingCreate(false);
        }
    }, [pendingCreate, isOneAsset, stage, store]);

    const onClickCreateAsset = async () => {
        await createAssetAsync();
    };

    const init = async () => {
        const resourceSettings = await getSettingData("Asset");
        if (compareVersions(stage.version, VERSION_CM_14991_ASSET_GROUPED_BY_MEDIA_TYPE_SETTINGS) >= 0)
            setIsOneAsset(resourceSettings?.Asset?.isAssetGroupedByType?.value !== "false");
    };

    useEffect(() => {
        init();

        return () => {
            revokeBlobUrls();
            dispatch(createAssetReset());
        };
    }, []);

    return (
        <>
            {s3FilesModalOpen &&
                (compareVersions(stage.version, VERSION_CM_15200_PROJECT_MULTI_ACCOUNT) >= 0 ? (
                    <DialogSelectS3FilesV2
                        open
                        onSubmit={onChangeFiles}
                        onClose={() => setS3FilesModalOpen(false)}
                        isMultiFileSelect={true}
                    />
                ) : (
                    <DialogSelectS3Files
                        open
                        onSubmit={onChangeFiles}
                        onClose={() => setS3FilesModalOpen(false)}
                        onlyLocal={""}
                        maximumSize={"5 GB"}
                        acceptFileExtension={"*"}
                        isMultiFileSelect={true}
                    />
                ))}
            <StickyHeaderDialog
                onClose={onHandleCloseAddAsset}
                open={open}
                variant="expandable"
                sx={{ "& .MuiDialog-paper": { maxWidth: "960px" } }}
            >
                <DialogTitle>
                    <Grid container>
                        <Typography variant="h3">{t(`common::label::Create`)}</Typography>
                    </Grid>
                    <TooltipButton
                        circled
                        title={t(`common::label::Close`)}
                        onClickButton={() => onHandleCloseAddAsset()}
                        buttonIcon={<IconClose size={20} htmlColor={theme.palette.secondary[700]} />}
                    />
                </DialogTitle>
                <DialogContent sx={{ "&&": { pr: "10px" } }}>
                    <Box>
                        <Grid
                            container
                            sx={{
                                width: "100%",
                                maxWidth: "1232px",
                                minHeight: "400px",
                                display: "grid",
                                gridTemplateRows: "auto 1fr",
                                pb: 2.5,
                            }}
                        >
                            <UploadProvider
                                open={false}
                                onChangeFiles={onChangeFiles}
                                disabled={pendingCreate}
                                disabledClick={true}
                                showDragSpacer={true}
                                isMultiFileSelect={true}
                            >
                                <Grid item>
                                    <Button
                                        variant={"outlined"}
                                        color={"primary"}
                                        rounded={true}
                                        onClick={() => setS3FilesModalOpen(true)}
                                        size={"small"}
                                    >
                                        {t(`common::label::Upload from cloud`, `Upload from cloud`)}
                                    </Button>
                                </Grid>
                            </UploadProvider>

                            <Grid container sx={{ gridArea: "2/1/3/2" }} gap={3}>
                                <CreateAssetFileSection
                                    pendingCreate={pendingCreate}
                                    isOneAsset={isOneAsset}
                                    setIsOneAsset={setIsOneAsset}
                                />

                                <CreateAssetLocationSection initialFolder={initialFolder} />

                                <CreateAssetMetadataSection
                                    pendingCreate={pendingCreate}
                                    initialCategory={initialCategory}
                                />

                                {!domainStatus.CUSTOM_CONTENTS_OFF && (
                                    <CreateAssetCustomMetadataSection
                                        pendingCreate={pendingCreate}
                                        initialCustomMetadata={initialCustomMetadata}
                                    />
                                )}
                            </Grid>
                        </Grid>
                    </Box>
                </DialogContent>
                <Divider />
                <DialogActions>
                    <VersionedComponent version={VERSION_CM_14175_ASSET_LOCK_WHILE_CREATE}>
                        <Stack flex={1} direction={"row"}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={isOwnerLocked}
                                        onChange={() => dispatch(updateData({ isOwnerLocked: !isOwnerLocked }))}
                                        color="greySecondary"
                                        disabled={pendingCreate}
                                    />
                                }
                                label={
                                    <Typography variant="body1_400" color={theme.palette.greySecondary.main}>
                                        {t(`common::msg::Lock this asset`, `Lock this asset`)}
                                    </Typography>
                                }
                                disabled={pendingCreate}
                                sx={{ m: 0 }}
                            />
                        </Stack>
                    </VersionedComponent>
                    <Box>
                        <Button
                            color="secondary"
                            onClick={() => onHandleCloseAddAsset()}
                            variant="text"
                            disabled={pendingCreate}
                        >
                            {t(`common::label::Cancel`, `Cancel`)}
                        </Button>
                        <Button
                            color="primary"
                            onClick={onClickCreateAsset}
                            variant="contained"
                            loading={pendingCreate}
                            disabled={
                                pendingCreate ||
                                customMetaDataRequiredError ||
                                selectedFilesEmpty ||
                                validationErrorsExist
                            }
                        >
                            {t(`common::label::Create`, `Create`)}
                        </Button>
                    </Box>
                </DialogActions>
            </StickyHeaderDialog>
        </>
    );
};

export default DialogCreateAsset;
